import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { errorMessage } from '../../services/apiError';
import { oauthService } from '../../services/oauthService';
import { GitHubRepositorySummary, RepositoryLinkResponse, projectService } from '../../services/projectService';
import { tokenService } from '../../services/tokenService';

import { FormField } from './FormField';
import { handleConnectionSubmit } from './formHelpers';
import { Dropdown } from '../Dropdown';

const REPOSITORY_LOOKUP_TIMEOUT_MS = 15000;

const repositoryValue = (repo: GitHubRepositorySummary): string => repo.clone_url || repo.html_url;

const repositoryName = (repo: GitHubRepositorySummary): string => repo.full_name.split('/').slice(1).join('/') || repo.full_name;

interface ConnectionFormProps {
  isLoading: boolean;
  onConnect: (
    repoUrl: string,
    username: string,
    token: string,
    createRepoIfMissing: boolean
  ) => Promise<void>;
  onCancel: () => void;
  initialRepoUrl?: string | null;
  initialUsername?: string | null;
  projectId?: string | null;
  onProjectSelected?: (projectId: string) => void;
}

export const ConnectionForm: React.FC<ConnectionFormProps> = ({
  isLoading,
  onConnect,
  onCancel,
  initialRepoUrl,
  initialUsername,
  projectId,
  onProjectSelected,
}) => {
  const [repoUrl, setRepoUrl] = useState(initialRepoUrl || '');
  const [username, setUsername] = useState(initialUsername || '');
  const [token, setToken] = useState('');
  const [createRepoIfMissing, setCreateRepoIfMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'native' | 'token'>('native');
  const [githubToken, setGithubToken] = useState<string | null>(null);
  const [githubLogin, setGithubLogin] = useState('');
  const [repositories, setRepositories] = useState<GitHubRepositorySummary[]>([]);
  const [repoLoading, setRepoLoading] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [repoSearch, setRepoSearch] = useState('');
  const [repositoryLink, setRepositoryLink] = useState<RepositoryLinkResponse | null>(null);
  const [repositoryLinkLoading, setRepositoryLinkLoading] = useState(false);

  const linkedProject = repositoryLink?.registered ? repositoryLink.project : null;
  const primaryActionLabel = linkedProject
    ? 'Go to project'
    : projectId
      ? 'Connect repository'
      : 'Create project';
  const primaryLoadingLabel = linkedProject
    ? 'Opening...'
    : projectId
      ? 'Connecting...'
      : 'Creating project...';
  const primaryDisabled = isLoading || repositoryLinkLoading || !repoUrl;

  const openLinkedProject = useCallback(() => {
    if (!linkedProject?.id) return;
    if (onProjectSelected) {
      onProjectSelected(linkedProject.id);
      return;
    }
    window.location.href = `/planning/${linkedProject.id}`;
  }, [linkedProject?.id, onProjectSelected]);

  const onSubmit = (e: React.FormEvent) => {
    if (linkedProject?.id) {
      e.preventDefault();
      openLinkedProject();
      return;
    }

    handleConnectionSubmit(e, repoUrl, username, token, createRepoIfMissing, setError, onConnect);
  };

  const selectedRepository = useMemo(
    () => repositories.find((repo) => repositoryValue(repo) === selectedRepo || repo.html_url === selectedRepo),
    [repositories, selectedRepo],
  );

  const ownerOptions = useMemo(() => {
    const seen = new Set<string>();

    return repositories
      .map((repo) => repo.full_name.split('/')[0])
      .filter((owner) => {
        if (!owner || seen.has(owner)) return false;
        seen.add(owner);

        return true;
      });
  }, [repositories]);

  const filteredRepositories = useMemo(() => {
    const ownerRepositories = selectedOwner
      ? repositories.filter((repo) => repo.full_name.startsWith(`${selectedOwner}/`))
      : repositories;
    const query = repoSearch.trim().toLowerCase();

    if (!query) return ownerRepositories;

    return ownerRepositories.filter((repo) => {
      const fullName = repo.full_name.toLowerCase();
      const nameOnly = repositoryName(repo).toLowerCase();

      return fullName.includes(query) || nameOnly.includes(query);
    });
  }, [repositories, repoSearch, selectedOwner]);

  const selectRepository = useCallback((repo?: GitHubRepositorySummary) => {
    const nextUrl = repo?.clone_url || repo?.html_url || '';
    setSelectedRepo(nextUrl);
    setRepoUrl(nextUrl);
  }, []);

  const loadRepositories = useCallback(async (accessToken: string) => {
    setRepoLoading(true);
    setError(null);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const timeout = new Promise<GitHubRepositorySummary[]>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('GitHub repository lookup timed out. Try again in a moment.'));
        }, REPOSITORY_LOOKUP_TIMEOUT_MS);
      });

      const items = await Promise.race([
        projectService.listGitHubRepositories(accessToken),
        timeout,
      ]);

      if (timeoutId) clearTimeout(timeoutId);

      setRepositories(items);
      setRepoSearch('');
      if (items.length > 0) {
        const firstRepo = items[0];
        setSelectedOwner(firstRepo.full_name.split('/')[0] || '');
        selectRepository(firstRepo);
      } else {
        setSelectedOwner('');
        setRepoSearch('');
        selectRepository(undefined);
        setError('No repositories were visible to this GitHub authorization. Try connecting another org/account or use a token with repo access.');
      }
    } catch (err) {
      setRepositories([]);
      setSelectedOwner('');
      setRepoSearch('');
      selectRepository(undefined);
      setError(errorMessage(
        err,
        'We couldn’t list repositories for this GitHub account. Reconnect GitHub and try again.',
      ));
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setRepoLoading(false);
    }
  }, [selectRepository]);

  useEffect(() => {
    const oauthError = sessionStorage.getItem('github_repo_connect_error');
    if (oauthError) {
      setError(oauthError);
      sessionStorage.removeItem('github_repo_connect_error');
    }

    const storedToken = sessionStorage.getItem('github_repo_connect_token');
    const storedLogin = sessionStorage.getItem('github_repo_connect_login') || '';
    if (!storedToken) return;

    setGithubToken(storedToken);
    setGithubLogin(storedLogin);
    setUsername(storedLogin);
    setToken(storedToken);
    setMode('native');
    if (!tokenService.getAccessToken()) {
      setRepositories([]);
      setSelectedOwner('');
      setRepoSearch('');
      selectRepository(undefined);
      setError('Reconnect GitHub so 8080.ai can finish account authorization before listing repositories.');
      return;
    }
    void loadRepositories(storedToken);
  }, [loadRepositories, selectRepository]);

  const handleNativeGitHubAuth = async () => {
    sessionStorage.removeItem('github_repo_connect_token');
    sessionStorage.removeItem('github_repo_connect_login');
    sessionStorage.removeItem('github_repo_connect_error');
    try {
      setError(null);
      window.location.href = await oauthService.getGitHubRepoConnectAuthUrl(window.location.href, projectId);
    } catch (err) {
      setError(errorMessage(err, 'We couldn’t start GitHub authorization. Check the GitHub configuration and try again.'));
    }
  };

  const handleRepositoryChange = (nextUrl: string) => {
    const nextRepo = repositories.find((repo) => repositoryValue(repo) === nextUrl || repo.html_url === nextUrl);

    selectRepository(nextRepo);
  };

  const handleOwnerChange = (nextOwner: string) => {
    setSelectedOwner(nextOwner);
    setRepoSearch('');
    const nextRepo = repositories.find((repo) => repo.full_name.startsWith(`${nextOwner}/`));
    selectRepository(nextRepo);
  };

  useEffect(() => {
    if (repoLoading) return;
    if (filteredRepositories.length === 0) {
      if (selectedRepo) selectRepository(undefined);
      return;
    }

    const hasVisibleSelection = filteredRepositories.some((repo) => repositoryValue(repo) === selectedRepo);
    if (!hasVisibleSelection) selectRepository(filteredRepositories[0]);
  }, [filteredRepositories, repoLoading, selectRepository, selectedRepo]);

  useEffect(() => {
    const lookupRepoUrl = repoUrl.trim();
    if (!lookupRepoUrl) {
      setRepositoryLink(null);
      setRepositoryLinkLoading(false);
      return undefined;
    }

    let cancelled = false;
    setRepositoryLink(null);
    setRepositoryLinkLoading(true);
    const lookupTimer = setTimeout(() => {
      projectService.lookupRepositoryLink(lookupRepoUrl)
        .then((result) => {
          if (!cancelled) setRepositoryLink(result);
        })
        .catch((err) => {
          if (!cancelled) {
            setRepositoryLink(null);
            console.warn('Failed to look up repository project link:', err);
          }
        })
        .finally(() => {
          if (!cancelled) setRepositoryLinkLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(lookupTimer);
    };
  }, [repoUrl]);

  const onNativeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (linkedProject?.id) {
      openLinkedProject();
      return;
    }

    if (!githubToken) {
      setError('Authorize GitHub first, or use an access token.');
      return;
    }
    if (!repoUrl) {
      setError('Select a repository to connect.');
      return;
    }

    try {
      setError(null);
      await onConnect(repoUrl, githubLogin, githubToken, createRepoIfMissing);
      sessionStorage.removeItem('github_repo_connect_token');
      sessionStorage.removeItem('github_repo_connect_login');
    } catch (err: unknown) {
      setError(errorMessage(
        err,
        'We couldn’t connect this GitHub repository. Verify your access and try again.',
      ));
    }
  };

  return (
    <div className="github-form">
      <div className="github-auth-mode">
        <button
          type="button"
          className={`github-auth-mode__button${mode === 'native' ? ' github-auth-mode__button--active' : ''}`}
          onClick={() => setMode('native')}
        >
          <i className="fa-brands fa-github" />
          GitHub login
        </button>
        <button
          type="button"
          className={`github-auth-mode__button${mode === 'token' ? ' github-auth-mode__button--active' : ''}`}
          onClick={() => setMode('token')}
        >
          <i className="fa-solid fa-key" />
          Auth token
        </button>
      </div>

      {mode === 'native' ? (
        <form onSubmit={onNativeSubmit} className="github-native-form">
          {githubToken ? (
            <div className="github-connected-account">
              <div>
                <span className="github-connected-account__eyebrow">Connected account</span>
                <strong>{githubLogin || 'GitHub user'}</strong>
              </div>
              <button type="button" onClick={handleNativeGitHubAuth} disabled={isLoading}>
                Connect another org
              </button>
            </div>
          ) : (
            <button type="button" className="github-native-button" onClick={handleNativeGitHubAuth} disabled={isLoading}>
              <i className="fa-brands fa-github" />
              Authorize with GitHub
            </button>
          )}

          {githubToken && (
            <div className="github-repo-picker">
              <label htmlFor="github-owner-select">Owner / organization</label>
              <Dropdown
                id="github-owner-select"
                value={selectedOwner}
                onChange={(event) => handleOwnerChange(event.target.value)}
                disabled={repoLoading || isLoading || ownerOptions.length === 0}
              >
                {repoLoading ? (
                  <option>Loading owners...</option>
                ) : ownerOptions.length === 0 ? (
                  <option value="">No owners found</option>
                ) : (
                  ownerOptions.map((owner) => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))
                )}
              </Dropdown>

              <label htmlFor="github-repo-search">Search repositories</label>
              <div className="github-repo-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  id="github-repo-search"
                  type="search"
                  value={repoSearch}
                  onChange={(event) => setRepoSearch(event.target.value)}
                  placeholder="Search by repository name"
                  disabled={repoLoading || isLoading || repositories.length === 0}
                />
                {repoSearch && (
                  <button type="button" onClick={() => setRepoSearch('')} aria-label="Clear repository search">
                    <i className="fa-solid fa-xmark" />
                  </button>
                )}
              </div>

              <label htmlFor="github-repo-select">Repository</label>
              <Dropdown
                id="github-repo-select"
                value={selectedRepo}
                onChange={(event) => handleRepositoryChange(event.target.value)}
                disabled={repoLoading || isLoading}
              >
                {repoLoading ? (
                  <option>Loading repositories...</option>
                ) : filteredRepositories.length === 0 ? (
                  <option value="">No repositories found</option>
                ) : (
                  filteredRepositories.map((repo) => (
                    <option key={repositoryValue(repo)} value={repositoryValue(repo)}>
                      {repo.full_name}
                    </option>
                  ))
                )}
              </Dropdown>
              {selectedRepository && (
                <div className="github-repo-selected">
                  <span className="github-repo-selected__name">{selectedRepository.full_name}</span>
                  <span className={`github-repo-visibility${selectedRepository.private ? ' github-repo-visibility--private' : ''}`}>
                    {selectedRepository.private ? 'Private' : 'Public'}
                  </span>
                  {selectedRepository.default_branch && (
                    <span className="github-repo-meta">Default branch: {selectedRepository.default_branch}</span>
                  )}
                </div>
              )}
              {repoUrl && (
                <div className="github-repo-project-state">
                  {repositoryLinkLoading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin" />
                      Checking project connection...
                    </>
                  ) : linkedProject ? (
                    <>
                      <i className="fa-solid fa-circle-check" />
                      Already connected to <strong>{linkedProject.title}</strong>
                    </>
                  ) : repositoryLink ? (
                    <>
                      <i className="fa-solid fa-circle-plus" />
                      No 8080 project is connected to this repository yet.
                    </>
                  ) : null}
                </div>
              )}
              {!repoLoading && error && repositories.length === 0 && (
                <button
                  type="button"
                  className="github-repo-retry"
                  onClick={() => loadRepositories(githubToken)}
                  disabled={isLoading}
                >
                  <i className="fa-solid fa-rotate-right" />
                  Retry loading repositories
                </button>
              )}
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={repoLoading || !githubToken || primaryDisabled}>
              {isLoading ? primaryLoadingLabel : repositoryLinkLoading ? 'Checking...' : primaryActionLabel}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="github-token-form">
          <FormField label="Repository URL" type="url" value={repoUrl} onChange={setRepoUrl} placeholder="https://github.com/username/repo.git" required />
          <FormField label="GitHub Username" value={username} onChange={setUsername} placeholder="username" />
          <FormField label="Access Token" type="password" value={token} onChange={setToken} placeholder="ghp_..." />

          <label className="github-checkbox-row">
            <input
              type="checkbox"
              checked={createRepoIfMissing}
              onChange={(event) => setCreateRepoIfMissing(event.target.checked)}
            />
            <span>Create the repository at this URL if it does not exist and the token permits it</span>
          </label>

          <a
            className="github-token-link"
            href="https://github.com/settings/tokens/new?description=8080.AI%20Repository%20Access&scopes=repo"
            target="_blank"
            rel="noopener noreferrer"
          >
            Generate a GitHub access token
          </a>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={primaryDisabled}>
              {isLoading ? primaryLoadingLabel : repositoryLinkLoading ? 'Checking...' : primaryActionLabel}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
